import { useState } from "react";
import { CircularProgress, Tooltip, FormControlLabel, Switch, TablePagination } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import CreateModal from "./CreateModal";
import { useUserInfo } from "../../../../context/user";
import EditModal from "./EditModal"
import DeleteModal from "./DeleteModal"
import { DataGrid, DataGridHeader, DataGridRow, DataGridCell } from "components/ui/data-grid";
import {
  useAccountLevelAgents,
  accountLevelAgentsKeys,
  useUpdateAgent,
  useUpdateDefaultAssistant,
  useUpdateAccountAssistant,
} from "../../../../queries/agents";
function Agents() {
  const [activeTab, setActiveTab] = useState("agent");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const accountPlugins = useUserInfo()[19];
  const userActiveLocation = useUserInfo()[4];
  const queryClient = useQueryClient();
  const updateAgent = useUpdateAgent();
  const updateDefaultAssistant = useUpdateDefaultAssistant();
  const updateAccountAssistant = useUpdateAccountAssistant();
  // Was AppService.assistant.getAccountlevelassistants(...) re-fetched (via a
  // localforage.getItem("active_location") lookup) on mount and after every
  // write; now backed by useAccountLevelAgents (src/queries/agents.js) — a
  // DIFFERENT endpoint/shape than useAgentsList (account-scoped, no user_id),
  // so it's its own query rather than forced into useAgentsList. isFetching
  // (not isLoading) preserves the original full-table spinner on every
  // refresh, not just the first load.
  const { data: agents = [], isFetching: loading } = useAccountLevelAgents(userActiveLocation?.id);
  const [toggleCheckedState, setToggleCheckedState] = useState(() => {
    const initialToggleState = {};
    agents?.forEach((item) => {
      initialToggleState[item._id] = item.status; // Initialize all switches to false (unchecked)
    });
    return initialToggleState;
  });

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0); // Reset to first page when rows per page is changed
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: accountLevelAgentsKeys.list(userActiveLocation?.id) });
  };

  const formatString = (str) => {
    // Replace underscores with spaces
    str = str.replace(/_/g, ' ');

    // Add space before each capital letter, ensuring no double spaces
    str = str.replace(/([a-z])([A-Z])/g, '$1 $2');

    // Trim and return the final result
    return str.trim();
  };

  const handleToggleChange = (id) => {
    // First, update the state with the new toggle value
    setToggleCheckedState((prevState) => {
      const newState = {
        ...prevState,
        [id]: !prevState[id],
      };
      updateAgent
        .mutateAsync({ _id: id, status: newState[id] })
        .then(() => handleRefresh())
        .catch((error) => {
          console.error("Error updating assistant:", error);
        });

      return newState;
    });
  };

  const handleToggleChangedefaultassistant = async (assistantid, currentState) => {
    try {
      const newState = !currentState; // Toggle the current state

      await updateDefaultAssistant.mutateAsync({
        assistantid,
        ui_enable: newState,
      });
      handleRefresh();
    } catch (error) {
      console.error("Error updating assistant:", error);
    }
  }

  const handleToggleChangedefaultaccount = async (assistantId, profileId, currentState, assistant_type) => {
    try {
      const newState = !currentState; // Toggle the current state

      await updateAccountAssistant.mutateAsync({
        assistantid: assistantId,
        profile_id: profileId,
        assitentdefault_value_change: newState,
        assistant_type: assistant_type,
      });
      handleRefresh();
    } catch (error) {
      console.error("Error updating assistant:", error);
    }
  };

  return (
    <>
      {loading ? (
        <CircularProgress style={{ display: "block", margin: "20px auto" }} />
      ) : activeTab === "agent" ? (
        <div className="">
          <div className="tableHeader">
            <div className="page-header">
              <h1 className="page-display text-3xl md:text-4xl">Agents</h1>
            </div>
            <CreateModal open={false} onClose={() => alert("Modal closed")} handleRefresh={handleRefresh} pluginsdata={accountPlugins} />
          </div>
          <DataGrid columns="1fr 1fr auto" minWidth={600} className="mt-10">
            <DataGridHeader>
              <DataGridCell style={{ textAlign: 'left' }}>Name</DataGridCell>
              <DataGridCell style={{ textAlign: 'left' }}>Plugins</DataGridCell>
              <DataGridCell style={{ textAlign: 'right' }}>Actions</DataGridCell>
            </DataGridHeader>
            {agents
              .slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage)
              .map((agent) => (
                <DataGridRow>
                  <DataGridCell style={{ textTransform: 'capitalize' }}>{agent.assistant_name}</DataGridCell>
                  <DataGridCell>{formatString(agent.function_names?.join(",")).toUpperCase() || "None"}</DataGridCell>
                  <DataGridCell className="flex items-center justify-end whitespace-normal">
                    <span>
                      <Tooltip title="Default Agent" arrow>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={agent?.finderofdefaultvalue?.default_assistant === undefined ? false : agent?.finderofdefaultvalue?.default_assistant} // Ensure it defaults to `false` if undefined
                              onChange={() => handleToggleChangedefaultaccount(
                                agent.assistant_id,
                                agent.profile_id,
                                agent?.finderofdefaultvalue?.default_assistant === undefined ? false : agent?.finderofdefaultvalue?.default_assistant,
                                agent?.assist_type
                              )}
                            />
                          }
                          label=""
                        />
                      </Tooltip>
                    </span>
                    <span>
                      <Tooltip title="Chat UI" arrow>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={agent?.ui_enable}
                              onChange={() => handleToggleChangedefaultassistant(
                                agent._id,
                                agent?.ui_enable // Pass current state
                              )}
                            />
                          }
                          label=""
                        />
                      </Tooltip>
                    </span>
                    <span>
                      <Tooltip title="On/Off" arrow>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={toggleCheckedState[agent._id] || agent?.status}
                              onChange={() => handleToggleChange(agent._id)}
                            />
                          }
                          label=""
                        />
                      </Tooltip>
                    </span>
                    <span
                      style={{ padding: "5px", cursor: "pointer" }}>
                      <EditModal item={agent} handleRefresh={handleRefresh} assistant_type={agent?.assistant_type} pluginsdata={accountPlugins} />
                    </span>

                    <span
                      style={{ padding: "5px", cursor: "pointer" }}>
                      <DeleteModal item={agent} handleRefresh={handleRefresh} />
                    </span>
                  </DataGridCell>
                </DataGridRow>
              ))}
          </DataGrid>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={agents.length}
            rowsPerPage={rowsPerPage}
            page={currentPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </div>
      ) : (
        <div>
          <h2>Assistants</h2>
          <p>Manage your assistants here.</p>
        </div>
      )}
    </>
  );
}

export default Agents;
